import { CardInVariant, TemplateInVariant, Variant } from '@space-cow-media/spellbook-client'

import { EDHRECIconsType } from '@/components/Icon/Icon'
import { ComboListDataType, RecCardType } from '@/types/card'
import { ComboData } from '@/types/page'

export type ComboPrerequisites = {
  /* Additional description of the prerequisite */
  description: string
  /* Zone either H, B, C, G, L, E or multiple of them */
  zones: (keyof typeof ZONE_MAP)[]
}

const NONPERMANENT_TYPES = ['instant', 'sorcery']

const ZONE_MAP = {
  B: 'on the battlefield',
  C: 'in the command zone',
  E: 'in exile',
  G: 'in your graveyard',
  H: 'in hand',
  L: 'in your library',
  M: 'needs mana',
}

export const ZONE_TO_ICON_MAP: Record<keyof typeof ZONE_MAP, EDHRECIconsType> = {
  B: 'chessBoard',
  C: 'chessRook',
  E: 'ban',
  G: 'skullCrossbones',
  H: 'hand',
  L: 'book',
  M: 'chartSimple',
}

const GAME_CHANGERS = [
  'Ad Nauseam',
  'Ancient Tomb',
  'Aura Shards',
  'Biorhythm',
  'Braids, Cabal Minion',
  'Chrome Mox',
  'Coalition Victory',
  'Consecrated Sphinx',
  'Crop Rotation',
  'Cyclonic Rift',
  'Demonic Tutor',
  'Drannith Magistrate',
  'Enlightened Tutor',
  'Farewell',
  'Field of the Dead',
  'Fierce Guardianship',
  'Force of Will',
  'Gamble',
  'Gifts Ungiven',
  'Glacial Chasm',
  'Grand Arbiter Augustin IV',
  'Grim Monolith',
  'Humility',
  'Imperial Seal',
  'Intuition',
  "Bolas's Citadel",
  "Gaea's Cradle",
  "Jeska's Will",
  "Lion's Eye Diamond",
  'Mana Vault',
  "Mishra's Workshop",
  'Mox Diamond',
  'Mystical Tutor',
  'Narset, Parter of Veils',
  'Natural Order',
  'Necropotence',
  'Notion Thief',
  'Opposition Agent',
  'Orcish Bowmasters',
  'Panoptic Mirror',
  'Rhystic Study',
  'Seedborn Muse',
  "Serra's Sanctum",
  'Smothering Tithe',
  'Survival of the Fittest',
  "Teferi's Protection",
  'Tergrid, God of Fright',
  "Thassa's Oracle",
  'The One Ring',
  'The Tabernacle at Pendrell Vale',
  'Underworld Breach',
  'Vampiric Tutor',
  'Worldly Tutor',
]

export function getName(card: CardInVariant | TemplateInVariant): string {
  return 'card' in card ? card.card.name : card.template.name
}

export function getNameBeforeComma(card: CardInVariant | TemplateInVariant): string {
  return 'card' in card ? card.card.name.split(', ')[0] : card.template.name
}

export function getTypes(card: CardInVariant | TemplateInVariant): string {
  return 'card' in card ? card.card.typeLine : ''
}

function comaAndOrJoin(input: string[], joiner = 'and') {
  if (input.length === 0) {
    return ''
  }
  if (input.length === 1) {
    return input[0]
  }
  if (input.length === 2) {
    return input.join(` ${joiner} `)
  }
  return `${input.slice(0, -1).join(', ')}, ${joiner} ${input.slice(-1)}`
}

function getCardState(card: CardInVariant | TemplateInVariant) {
  const output: Array<{ state: string; zone: keyof typeof ZONE_MAP }> = []
  if (card.battlefieldCardState) {
    output.push({ state: card.battlefieldCardState, zone: 'B' })
  }
  if (card.exileCardState) {
    output.push({ state: card.exileCardState, zone: 'E' })
  }
  if (card.graveyardCardState) {
    output.push({ state: card.graveyardCardState, zone: 'G' })
  }
  if (card.libraryCardState) {
    output.push({ state: card.libraryCardState, zone: 'L' })
  }
  if (output.length === 0) {
    return ''
  }
  if (output.length === 1) {
    return output[0].state
  }
  return comaAndOrJoin(
    output.map((o) => `${o.state} ${ZONE_MAP[o.zone]}`),
    'or',
  )
}

type Card = (CardInVariant | TemplateInVariant) & { name: string; type: string }

// TODO: consider DFCs

export const getPrerequisiteList = (variant: Variant): ComboPrerequisites[] => {
  const cardsAndTemplates: Array<Card> = (variant.uses as Array<CardInVariant | TemplateInVariant>)
    .concat(variant.requires)
    .map((card) => ({ ...card, name: getName(card), type: getTypes(card) }))

  // Count if any coma split card names exist more than once
  const cardNameCountMap = cardsAndTemplates.reduce((acc: Record<string, number>, card) => {
    const split = getNameBeforeComma(card)
    acc[split] = acc[split] ? acc[split] + 1 : 1
    return acc
  }, {})

  // Map card names to coma split card names if they only exist once
  const cardNameMap = cardsAndTemplates.reduce((acc: Record<string, string>, card) => {
    const split = getNameBeforeComma(card)
    acc[card.name] = cardNameCountMap[split] === 1 ? split : card.name
    return acc
  }, {})

  // Handle zones descriptions
  const zonesToDescriptions: Record<string, string> = cardsAndTemplates.reduce((acc: Record<string, string>, card) => {
    const zoneKey = card.zoneLocations.join('')
    if (acc[zoneKey]) {
      return acc
    }
    if (Object.keys(ZONE_MAP).length === card.zoneLocations.length) {
      acc[zoneKey] = 'in any zone'
    } else {
      acc[zoneKey] = card.zoneLocations.map((zone) => ZONE_MAP[zone as keyof typeof ZONE_MAP]).join(' or ')
    }
    return acc
  }, {})

  // Sort cards into groups by zone
  const zoneGroups: Array<{ cardState: string; cards: Card[]; zones: string[] }> = []
  for (const zoneKey of Object.keys(zonesToDescriptions).toSorted((a, b) =>
    a.length == b.length
      ? a
          .split('')
          .map((c) => Object.keys(ZONE_MAP).indexOf(c).toString())
          .join('')
          .localeCompare(
            b
              .split('')
              .map((c) => Object.keys(ZONE_MAP).indexOf(c).toString())
              .join(''),
          )
      : a.length - b.length,
  )) {
    const zoneCards = cardsAndTemplates.filter((card) => card.zoneLocations.join('') === zoneKey)
    const reverseCardStateMap: Record<string, Card[]> = zoneCards.reduce((acc: Record<string, Card[]>, card) => {
      const state = getCardState(card)
      if (state) {
        if (acc[state]) {
          acc[state].push(card)
        } else {
          acc[state] = [card]
        }
      }
      return acc
    }, {})
    const cardStateStrings = Object.keys(reverseCardStateMap).map(
      (stateKey) =>
        (zoneCards.length > 1 && reverseCardStateMap[stateKey].length < zoneCards.length
          ? comaAndOrJoin(reverseCardStateMap[stateKey].map((card) => cardNameMap[card.name])) + ' '
          : '') + stateKey,
    )
    const cardState = comaAndOrJoin(cardStateStrings)
    zoneGroups.push({
      cards: zoneCards,
      cardState,
      zones: zoneKey.split(''),
    })
  }
  let index = 0
  const output: ComboPrerequisites[] = []
  for (const zoneGroup of zoneGroups.sort((a, b) => a.cards.length - b.cards.length)) {
    const cards = zoneGroup.cards.map(
      (card) => `${card.quantity > 1 ? `${card.quantity}x ` : ''}${cardNameMap[card.name]}`,
    )
    // If this is the last zone group, and it has more than 2 cards, swap card names for combinations string
    const stateBit = zoneGroup.cardState ? ` (${zoneGroup.cardState})` : ''
    if (index === zoneGroups.length - 1 && zoneGroup.cards.length > 2) {
      const otherGroups = zoneGroups.slice(0, -1)
      const otherGroupsHaveAPermanent = otherGroups.some((g) =>
        g.cards.some((c) => !NONPERMANENT_TYPES.some((t) => c.type.toLowerCase().includes(t))),
      )
      const thisGroupIsOfPermanents = zoneGroup.cards.every((c) => !NONPERMANENT_TYPES.some((t) => c.type.includes(t)))
      const description = `All${zoneGroups.length > 1 && (!thisGroupIsOfPermanents || otherGroupsHaveAPermanent) ? ' other' : ''} ${thisGroupIsOfPermanents ? 'permanents' : 'cards'} ${zonesToDescriptions[zoneGroup.zones.join('')]}${stateBit}.`
      output.push({
        description: description,
        zones: zoneGroup.zones as (keyof typeof ZONE_MAP)[],
      })
    } else {
      // Otherwise just list the cards
      output.push({
        description:
          (cards.length < 3 ? cards.join(' and ') : cards.slice(0, -1).join(', ') + ' and ' + cards.slice(-1)) +
          ' ' +
          zonesToDescriptions[zoneGroup.zones.join('')] +
          stateBit +
          '.',
        zones: zoneGroup.zones as (keyof typeof ZONE_MAP)[],
      })
    }
    index++
  }
  // Add any other prerequisites
  const commanders = cardsAndTemplates.filter((card) => card.mustBeCommander).map((card) => cardNameMap[card.name])
  if (commanders.length > 0) {
    output.push({
      description: `${comaAndOrJoin(commanders)} must be your commander${commanders.length > 1 ? 's' : ''}.`,
      zones: ['C'],
    })
  }

  if (variant.easyPrerequisites) {
    variant.easyPrerequisites.split(/\.\s+/gi).forEach((prereq) => output.push({ description: prereq, zones: [] }))
  }
  if (variant.notablePrerequisites) {
    variant.notablePrerequisites.split(/\.\s+/gi).forEach((prereq) => output.push({ description: prereq, zones: [] }))
  }

  if (variant.manaNeeded) {
    output.push({ description: `${variant.manaNeeded} available`, zones: ['M'] })
  }

  return output
}

export const ordinalSuffix = (i: number) => {
  const j = i % 10,
    k = i % 100
  if (j === 1 && k !== 11) {
    return i + 'st'
  }
  if (j === 2 && k !== 12) {
    return i + 'nd'
  }
  if (j === 3 && k !== 13) {
    return i + 'rd'
  }
  return i + 'th'
}

const isContested = (score: number) => {
  return score >= 0.4 && score < 0.5
}

export const comboTierState = (combo: ComboListDataType | ComboData, cards: RecCardType[]) => {
  const gameChangerCount = cards.filter((card) => GAME_CHANGERS.includes(card.name)).length
  let lowTierState: 'error' | 'success' | 'warning' = 'error'
  let lowTierMessage = 'No 2-card infinite combos'
  if (gameChangerCount) {
    lowTierState = 'error'
    lowTierMessage = `This combo requires one or more Game Changers, which are not allowed in Brackets 1 and 2.`
  } else if (combo.cardIds.length > 2) {
    lowTierState = 'success'
    lowTierMessage = 'Any combos that require three or more cards are allowed'
  } else if (combo.cardIds.length == 2 && combo.comboVote) {
    if (isContested(combo.comboVote.prereqScore) && combo.comboVote.resultScore >= 0.5) {
      lowTierState = 'warning'
      lowTierMessage =
        "The community has determined this isn't a true 2-card combo because the prerequisites are nontrivial. However, it was a close vote. Play with caution."
    } else if (isContested(combo.comboVote.resultScore) && combo.comboVote.prereqScore >= 0.5) {
      lowTierState = 'warning'
      lowTierMessage =
        "The community has determined this isn't a true 2-card combo because the results are not significant. However, it was a close vote. Play with caution."
    } else if (isContested(combo.comboVote.resultScore) && isContested(combo.comboVote.prereqScore)) {
      lowTierState = 'warning'
      lowTierMessage =
        "The community has determined this isn't a true 2-card combo because the prerequisites are nontrivial and the results are not significant. However, it was a close vote. Play with caution."
    } else if (combo.comboVote.bracket === 'any') {
      lowTierState = 'success'
      lowTierMessage = "The community has determined this isn't a true 2-card combo."
    } else {
      lowTierState = 'error'
      lowTierMessage = 'The community has determined this is a true 2-card combo.'
    }
  } else if (combo.cardIds.length == 2 && !combo.comboVote) {
    lowTierState = 'warning'
    lowTierMessage =
      "No early game, 2-card infinite combos, but the community hasn't weighed in yet if this one counts."
  }

  let midTierState: 'error' | 'success' | 'warning' = 'error'
  let midTierMessage = 'No early game, 2-card infinite combos'
  if (gameChangerCount > 3) {
    midTierState = 'error'
    midTierMessage = `This combo requires over three Game Changers. You may have a maximum of three Game Changers in Bracket 3.`
  } else if (combo.cardIds.length > 2) {
    midTierState = 'success'
    midTierMessage = 'Any combos that require 3 or more cards are allowed'
  } else if (combo.cardIds.length == 2 && combo.comboVote) {
    if (lowTierState === 'warning' && combo.comboVote.earlyScore < 0.4) {
      midTierState = 'success'
      midTierMessage =
        "The community has determined this isn't a true 2-card combo in a close vote. However, it was clear that it's not early game, so it's safe regardless."
    } else if (lowTierState === 'warning' && isContested(combo.comboVote.earlyScore)) {
      midTierState = 'warning'
      midTierMessage =
        "The community has determined this isn't a true 2-card combo in a close vote and that it's not early game in a close vote. Play with caution."
    } else if (lowTierState === 'warning') {
      midTierState = 'warning'
      midTierMessage =
        "The community has determined this isn't a true 2-card combo in a close vote while it is an early game combo. Play with caution."
    } else if (isContested(combo.comboVote.earlyScore) && lowTierState === 'success') {
      midTierState = 'warning'
      midTierMessage =
        "The community has determined this isn't an early game combo. However, it was a close vote. Play with caution."
    } else if (combo.comboVote.bracket === 'any') {
      midTierState = 'success'
      midTierMessage = "The community has determined this isn't a true 2-card combo."
    } else if (combo.comboVote.bracket === '3') {
      midTierState = 'success'
      midTierMessage = "The community has determined this isn't an early game combo."
    } else {
      midTierState = 'error'
      midTierMessage = 'The community has determined this is a true, early game, 2-card combo.'
    }
  } else if (combo.cardIds.length == 2 && !combo.comboVote) {
    midTierState = 'warning'
    midTierMessage =
      "No early game, 2-card infinite combos, but the community hasn't weighed in yet if this one counts."
  }

  return {
    lowTierMessage,
    lowTierState,
    midTierMessage,
    midTierState,
  }
}

export const formatResult = (result: string) => {
  return result.replace('Near-infinite', 'Significant').replace('near-infinite', 'significant')
}
